'use client'

import { cn } from '@brew/ui/cn'
import { Play } from 'lucide-react'
import { useState } from 'react'
import {
  MK_BODY,
  MK_LABEL,
  MK_SMALL,
} from '@/components/marketing/home/home-layout'
import {
  formatVideoLength,
  type UniversityVideo,
  videoUrlAt,
} from '@/lib/site/university-content'

const PRESENTER_ROLE = {
  Philip: 'Philip, CEO',
  Thomas: 'Thomas, CTO',
} as const

/**
 * One Brew University video: YouTube's thumbnail until someone presses
 * play, so the page loads no player it does not need, then the
 * privacy-enhanced embed. Each chapter starts the same player at its
 * moment. `isCompact` drops the chapters, for a video shown beside a related
 * page rather than on /university.
 */
export function VideoCard({
  video,
  isCompact = false,
  className,
}: {
  video: UniversityVideo
  isCompact?: boolean
  className?: string
}) {
  // Each play is its own request, so pressing the same chapter again
  // remounts the player and restarts it there.
  const [play, setPlay] = useState<{ start: number; n: number } | null>(null)
  const playFrom = (start: number) =>
    setPlay((current) => ({ start, n: (current?.n ?? 0) + 1 }))
  const length = formatVideoLength(video.seconds)

  return (
    <article
      className={cn(
        'flex min-w-0 flex-col gap-5 overflow-clip rounded-3xl bg-mk-tile p-4 shadow-[0_0_0_1px_var(--mk-line)] sm:p-5',
        className
      )}
    >
      <div className="relative aspect-video w-full overflow-clip rounded-2xl bg-[#0b0a10]">
        {play === null ? (
          <button
            type="button"
            onClick={() => playFrom(0)}
            className="group/play absolute inset-0 flex items-center justify-center focus-visible:outline-2 focus-visible:outline-mk-accent focus-visible:outline-offset-2"
            aria-label={`Play ${video.title} (${length})`}
          >
            {/* biome-ignore lint/performance/noImgElement: YouTube's own thumbnail, cropped to 16:9 */}
            <img
              src={`https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`}
              alt=""
              width={480}
              height={360}
              loading="lazy"
              className="absolute inset-0 size-full object-cover"
            />
            <span className="relative flex size-14 items-center justify-center rounded-full bg-white/90 text-[#171717] shadow-lg transition-transform duration-quick ease-smooth-out group-hover/play:scale-105 motion-reduce:transition-none">
              <Play aria-hidden="true" className="ml-0.5 size-6 fill-current" />
            </span>
            <span className="absolute right-3 bottom-3 rounded-md bg-black/75 px-1.5 py-0.5 font-medium font-mk-body text-[12px] text-white tabular-nums">
              {length}
            </span>
          </button>
        ) : (
          <iframe
            key={play.n}
            src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0&start=${play.start}`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 size-full"
          />
        )}
      </div>

      <div className="flex flex-col gap-2 px-1">
        <h3 className={cn(MK_LABEL, 'text-mk-fg')}>{video.title}</h3>
        {video.presenter ? (
          <p className={cn(MK_SMALL, 'text-mk-faint')}>
            With {PRESENTER_ROLE[video.presenter]} · {length}
          </p>
        ) : (
          <p className={cn(MK_SMALL, 'text-mk-faint')}>{length}</p>
        )}
        <p className={cn(MK_BODY, 'text-mk-body')}>{video.description}</p>
        <a
          href={videoUrlAt(video)}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            MK_SMALL,
            'self-start text-mk-accent underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-mk-accent'
          )}
        >
          Watch on YouTube <span aria-hidden="true">↗</span>
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      </div>

      {!isCompact && video.chapters.length > 0 ? (
        <ol className="mt-auto flex flex-col border-mk-line border-t px-1 pt-3">
          {video.chapters.map((chapter) => (
            <li key={chapter.t}>
              <button
                type="button"
                onClick={() => playFrom(chapter.t)}
                className={cn(
                  MK_SMALL,
                  'flex w-full items-baseline gap-3 py-1.5 text-left text-mk-body hover:text-mk-accent focus-visible:outline-2 focus-visible:outline-mk-accent'
                )}
              >
                <span className="w-10 shrink-0 font-mk-body text-mk-accent tabular-nums">
                  {formatVideoLength(chapter.t)}
                </span>
                <span>{chapter.title}</span>
              </button>
            </li>
          ))}
        </ol>
      ) : null}
    </article>
  )
}
